// Edit-panel field "width spaces" model.
//
// The edit panel is a horizontal track made of a whole number of equal-width
// "spaces". Each field declares how many spaces it intrinsically wants to
// occupy (e.g. a toggle wants 1, a two-option segmented control wants 2, a rich
// editor wants the whole row). The panel exposes a *capacity* — the number of
// spaces that fit across its current pixel width — and consecutive fields are
// packed left-to-right onto a line until the next field no longer fits, at
// which point it wraps to a new line.
//
// Narrow panels collapse to a capacity of 1, so every field ends up alone on
// its own line (a switch occupies the single available space). Wider panels let
// compatible controls (two toggles, a colour + a slider, …) share a line.

// A field with this width always consumes the full line regardless of capacity.
// Represented as Infinity so `Math.min(span, capacity)` naturally clamps it to
// the panel capacity.
export const FIELD_WIDTH_FULL = Infinity;

// Approximate minimum comfortable pixel width for a single "space" (a compact
// control such as a labelled toggle). Capacity is derived by dividing the
// panel's usable width by this unit. Kept small enough that three toggles fit
// across a typical (often indented/nested) edit column.
export const EDIT_PANEL_SPACE_UNIT_PX = 100;

// Hard ceiling on how many spaces a single line may contain. Keeps very wide
// panels from cramming an unreadable number of controls onto one line.
export const EDIT_PANEL_MAX_SPACES = 4;

// Intrinsic span (number of spaces) per field appearance. Any appearance not
// listed here defaults to FIELD_WIDTH_FULL (its own line), which preserves the
// previous one-field-per-line behaviour for rich/complex editors.
const APPEARANCE_WIDTH_SPACES = {
  // Single compact controls — happy to sit one-per-space.
  switch: 1,
  checkbox: 1,
  float: 1,
  integer: 1,
  big_float: 1,
  big_number: 1,

  // Medium controls — need a little more room (label + control or a couple of
  // side-by-side options) but still pair up nicely on wider panels.
  choices: 2,
  slider: 2,
  small_text: 2,
  color: 2,
  special_left_sidebar_toggle_color: 2,
  special_left_sidebar_toggle_hover_color: 2,
  special_right_sidebar_toggle_color: 2,
  special_right_sidebar_toggle_hover_color: 2,
  special_image_shape: 2,
  // Top/right/bottom/left spacing box (the simple, non-responsive margin/padding
  // control) — compact enough to share a line. The responsive margin/padding
  // wrappers use `special_by_layout` instead and are sized via
  // `looksLikeSpacingByLayout` below so both variants behave the same.
  special_spacing: 2,
  // Two-option (boolean) segmented controls rendered via BooleanChoiceField.
  special_image_or_pattern: 2,
  special_manual_or_automatic: 2,
  special_icon_position: 2,
  special_tight_or_loose: 2,
};

const isPositiveInteger = (value) =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

// The four sides every spacing (margin/padding) row exposes.
const SPACING_SIDES = ["top", "right", "bottom", "left"];

/**
 * Detect a responsive spacing wrapper (ResponsiveMargin / ResponsivePadding).
 *
 * These render through the generic `special_by_layout` appearance, so the
 * appearance string alone can't size them (it's shared with Layout, sliders,
 * integers, text-position, …). We anchor on the exact metadata *shape* — same
 * test `EditField.js` uses to route to `ByLayoutSpacingField`: every
 * per-breakpoint child is `appearance="nested"` exposing top/right/bottom/left.
 * This lets margin + padding be sized like the simple `special_spacing` box so
 * they can pair on one line when the panel is wide enough.
 *
 * @param {Object} metadata - Field metadata.
 * @returns {boolean}
 */
const looksLikeSpacingByLayout = (metadata) => {
  if ((metadata?.appearance || "").toString() !== "special_by_layout") return false;
  const nestedFields = metadata?.nested_fields;
  if (!nestedFields || typeof nestedFields !== "object") return false;

  const childMetas = Object.values(nestedFields);
  if (childMetas.length === 0) return false;

  return childMetas.every((child) => {
    if ((child?.appearance || "").toString() !== "nested") return false;
    const grandchildren = child?.nested_fields || {};
    return SPACING_SIDES.every((side) =>
      Object.prototype.hasOwnProperty.call(grandchildren, side),
    );
  });
};

/**
 * Resolve how many spaces a field intrinsically wants to occupy.
 *
 * Resolution order:
 *   1. An explicit `appearance_width_spaces` on the metadata (a positive
 *      integer, or the string "full" for a whole line). This lets the backend
 *      metadata override the default sizing per field.
 *   2. The appearance-based default map.
 *   3. FIELD_WIDTH_FULL for anything unknown (its own line).
 *
 * @param {Object} metadata - Field metadata (`appearance`, optional override).
 * @returns {number} A positive integer span, or FIELD_WIDTH_FULL.
 */
export const getFieldWidthSpaces = (metadata) => {
  const override = metadata?.appearance_width_spaces;
  if (isPositiveInteger(override)) return override;
  if (typeof override === "string" && override.trim().toLowerCase() === "full") {
    return FIELD_WIDTH_FULL;
  }

  // Responsive margin/padding share the `special_by_layout` appearance, so they
  // need shape-based detection rather than an entry in the appearance map.
  if (looksLikeSpacingByLayout(metadata)) return 2;

  const appearance = metadata?.appearance;
  if (appearance && Object.prototype.hasOwnProperty.call(APPEARANCE_WIDTH_SPACES, appearance)) {
    return APPEARANCE_WIDTH_SPACES[appearance];
  }
  return FIELD_WIDTH_FULL;
};

/**
 * Derive the panel capacity (spaces per line) from its usable pixel width.
 *
 * @param {number} pixelWidth - Usable content width of the panel/grid in px.
 * @returns {number} An integer in the range [1, EDIT_PANEL_MAX_SPACES].
 */
export const computePanelSpaceCapacity = (pixelWidth) => {
  if (!Number.isFinite(pixelWidth) || pixelWidth <= 0) return 1;
  const raw = Math.floor(pixelWidth / EDIT_PANEL_SPACE_UNIT_PX);
  return Math.min(Math.max(raw, 1), EDIT_PANEL_MAX_SPACES);
};

/**
 * Resolve the effective span a field occupies on a line of the given capacity.
 * Full-width fields and fields wider than the capacity are clamped to the
 * capacity so they always fill (at most) a whole line.
 *
 * @param {Object} metadata - Field metadata.
 * @param {number} capacity - Spaces available per line.
 * @returns {number} An integer in the range [1, capacity].
 */
export const getEffectiveFieldSpan = (metadata, capacity) => {
  const safeCapacity = Math.max(1, Math.floor(capacity) || 1);
  return Math.min(getFieldWidthSpaces(metadata), safeCapacity);
};

/**
 * Greedily pack fields onto lines, preserving order. A field is appended to the
 * current line while it still fits within `capacity`; otherwise it starts a new
 * line. Each returned line carries the field's *effective* span plus the line's
 * total span, so the renderer can stretch a partially-filled line's fields to
 * fill the full width (e.g. three 1-space fields on a 4-space panel expand to
 * occupy the whole row rather than leaving a trailing gap).
 *
 * @param {Array}  fields   - [{ field, metadata }] in display order.
 * @param {number} capacity - Spaces available per line.
 * @returns {Array<{ cells: Array<{ field, metadata, span }>, total: number }>}
 */
export const packFieldsIntoRows = (fields, capacity) => {
  const safeCapacity = Math.max(1, Math.floor(capacity) || 1);
  const rows = [];
  let cells = [];
  let total = 0;

  (fields || []).forEach(({ field, metadata }) => {
    const span = getEffectiveFieldSpan(metadata, safeCapacity);
    if (cells.length > 0 && total + span > safeCapacity) {
      rows.push({ cells, total });
      cells = [];
      total = 0;
    }
    cells.push({ field, metadata, span });
    total += span;
  });

  if (cells.length > 0) rows.push({ cells, total });
  return rows;
};
